import { partnersLimits } from "configs/limits";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "store";
import CmsPartnersFilter from "./components/CmsPartnersFilter";
import { CmsPartnersHeader } from "./components/CmsPartnersHeader";
import { Table } from "components/table/Table";
import { SortOrder, updateSort } from "helpers/updateSort";
import {
  handlePaginate,
  handleSelectAll as handleSelectAllItems,
  handleSelect as handleSelectItem,
  deletePartnerItem,
  type Partner,
  getPartnersList,
  bunchUpdatePartners,
} from "store/cms/footer/partners/slice";
import Pagination from "components/Pagination";
import { openModal } from "store/modals/slice";
import { ModalTypes } from "configs/types";
import EmptyState from "components/EmptyState/EmptyState";
import DataTableSkeleton from "components/global/Skeleton/TableSkeleton/DataTableSkeleton";
import PaginationSkeleton from "components/global/Skeleton/PaginationSkeleton/PaginationSkeleton";
import { usePermissionHelper } from "helpers/usePermissionHelper";
import { useRefreshList } from "helpers/useRefreshList";

const PartnersList: React.FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [limit, setLimit] = useState<number>(partnersLimits[0]);
  const [filterByTitle, setFilterByTitle] = useState<string>("");
  const [filterByGeo, setFilterByGeo] = useState<
    { value: string | number; label: string }[]
  >([]);
  const [filterByActive, setFilterByActive] = useState<
    { value: string | number; label: string }[]
  >([]);
  const [order, setOrder] = useState<SortOrder<Omit<Partner, "items">>>({});

  const partnersList = useAppSelector((state) => state["cms-partners"].list);
  const isPartnersListFetched = useAppSelector(
    (state) => state["cms-partners"].isListFetched,
  );
  const totalItems = useAppSelector(
    (state) => state["cms-partners"].totalItems,
  );
  const totalPages = useAppSelector(
    (state) => state["cms-partners"].totalPages,
  );
  const activePage = useAppSelector(
    (state) => state["cms-partners"].activePage,
  );
  const selectedItems = useAppSelector(
    (state) => state["cms-partners"].selectedItems,
  );
  const processingItemId = useAppSelector(
    (state) => state["cms-partners"].processingItemId,
  );

  const actionOptions = [
    { label: "Activate", value: "activate" },
    { label: "Hide", value: "hide" },
  ];

  const permissions = usePermissionHelper();
  const { refresh, isRefreshRequested } = useRefreshList({
    sliceName: 'cms-partners',
    isListFetched: isPartnersListFetched
  });

  useEffect(() => {
    if (!isPartnersListFetched) {
      dispatch(
        getPartnersList({
          limit,
          offset: activePage - 1,
          title: filterByTitle,
          countries: filterByGeo.map((item) => item.value) as string[],
          isActive: getActiveValue(filterByActive),
          order,
        }),
      );
    }
  }, [isPartnersListFetched, activePage, limit]);

  const handleOrderClick = (key: keyof typeof order) => {
    setOrder((currentOrder) => updateSort(currentOrder, key));
    dispatch(handlePaginate(1));
  };

  const handleSelectAll = () => {
    dispatch(handleSelectAllItems());
  };

  const handleSelect = (id: number) => {
    dispatch(handleSelectItem(id));
  };

  const handlePreviousClick = () => {
    if (activePage > 1) {
      dispatch(handlePaginate(activePage - 1));
    }
  };

  const handleNextClick = () => {
    if (activePage < totalPages) {
      dispatch(handlePaginate(activePage + 1));
    }
  };

  const handleApplyFilter = () => {
    dispatch(handlePaginate(1));
  };

  const handleOpenCreateModal = () => {
    navigate(`${location.pathname}?activeModal=${ModalTypes.partner}`);

    dispatch(openModal({ activeModal: ModalTypes.partner }));
  };

  const handleEditModal = (id: number) => {
    navigate(
      `${location.pathname}?activeModal=${ModalTypes.partner}&entityId=${id}`,
    );

    dispatch(openModal({ activeModal: ModalTypes.partner }));
  };

  const getActiveValue = (
    active: { value: string | number; label: string }[],
  ) => {
    if (!active.length || active.length > 1) return undefined;

    return +active[0]?.value;
  };

  const handleDelete = (id: Partner["id"]) => {
    dispatch(deletePartnerItem(id));
  };

  const handleSelectAction = async (value: string) => {
    if (!selectedItems.length) return;

    const isActive = value === "activate" ? 1 : 0;

    await dispatch(
      bunchUpdatePartners({
        ids: selectedItems,
        isActive,
      }),
    );

    dispatch(
      getPartnersList({
        limit,
        offset: activePage - 1,
        title: filterByTitle,
        countries: filterByGeo.map((g) => g.value) as string[],
        isActive: getActiveValue(filterByActive),
        order,
      }),
    );
  };

  const actionProps = permissions?.update
  ? {
      onEdit: (id: number) => handleEditModal(id),
      onView: undefined,
    }
  : {
      onEdit: undefined,
      onView: (id: number) => handleEditModal(id),
    };

  return (
    <div className="page-list-wrapper page-list--with-filter">
      <CmsPartnersFilter
        filterByTitle={filterByTitle}
        onFilterSearchByTitle={setFilterByTitle}
        filterByActive={filterByActive}
        onFilterByActive={setFilterByActive}
        filterByGeo={filterByGeo}
        onFilterByGeo={setFilterByGeo}
        handleApply={handleApplyFilter}
      />
      <div className="page-list--with-filter-content">
        <CmsPartnersHeader
          options={actionOptions}
          selectAction={handleSelectAction}
          createModal={handleOpenCreateModal}
          isDisabled={selectedItems.length === 0 || !permissions?.update}
          isCreateAllowed={permissions?.create}
          refresh={refresh}
          isRefreshRequested={isRefreshRequested}
        />

        {!isPartnersListFetched && (
          <>
            <DataTableSkeleton columnsCount={6} rowsCount={6} />
            <PaginationSkeleton />
          </>
        )}

        {isPartnersListFetched && partnersList.length === 0 && (
          <div className="game-table-wrap">
            <EmptyState message={t("noPartnerFound")} />
          </div>
        )}

        {isPartnersListFetched && partnersList.length > 0 && (
          <div className="game-table-wrap">
            <Table
              data={partnersList}
              idField="id"
              order={order}
              onSort={(v) => {
                const k = v as unknown;
                handleOrderClick(k as keyof typeof order);
              }}
              columns={{
                sortingColumns: {
                  geo: {
                    value: "geo",
                    hasSorting: true,
                  },
                },
              }}
              onAllSelect={handleSelectAll}
              onSelect={handleSelect}
              selectedItems={selectedItems}
              processingId={processingItemId}
              onDelete={handleDelete}
              hasDelete={permissions?.delete}
              hasEdit={permissions?.update}
              hasView={permissions?.read && !permissions?.update}
              hasOptions={permissions?.read || permissions?.update}
              {...actionProps}
            />
          </div>
        )}

        {isPartnersListFetched && partnersList.length > 0 && (
          <Pagination
            handleLimitClick={(e) => {
              setLimit(e);
              dispatch(handlePaginate(1));
            }}
            limitsArray={partnersLimits}
            activePage={activePage}
            limit={limit}
            totalItems={totalItems}
            totalPages={totalPages}
            handleNextClick={handleNextClick}
            handlePrevClick={handlePreviousClick}
          />
        )}
      </div>
    </div>
  );
};

export default PartnersList;
